import React, {useEffect} from 'react'
import {Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch} from 'react-hook-form'
import {deleteWizardDocument, downloadWizardDocument, uploadWizardDocument} from '../../api'
import {IApplicationFormData} from '../../models/application-form'
import {TextInput} from '../../../common/component/TextInput'
import WizardSectionCard from './sections/WizardSectionCard'
import UploadSlotRow from './sections/UploadSlotRow'
import {UPLOAD_SLOT_TYPES, UploadSlotDefinition} from './sections/uploadSlots'
import {RadioButtonInput} from '../../../common/component/RadioButtonInput'
import {toOptionalBoolean} from '../wizard/wizardAccess'
import {SelectInput} from '../../../common/component/SelectInput'
import {WheelInput} from '../../../common/component/WheelInput'
import {MobileNumberInput} from '../../../common/component/MobileNumberInput'

interface RentalHistoryStepProps {
  register: UseFormRegister<IApplicationFormData>
  control: Control<IApplicationFormData>
  errors?: FieldErrors<IApplicationFormData>
  setValue: UseFormSetValue<IApplicationFormData>
  watch: UseFormWatch<IApplicationFormData>
  triggerValidation?: () => Promise<boolean>
}

const rentDurationOptions = [
  {label: 'Less than 6 months', value: 'Less than 6 months'},
  {label: '6-12 months', value: '6-12 months'},
  {label: '1 year', value: '1 year'},
  {label: '2 years', value: '2 years'},
  {label: '3+ years', value: '3+ years'},
]

const RentalHistoryStep: React.FC<RentalHistoryStepProps> = ({
  register,
  control,
  errors,
  setValue,
  watch,
  triggerValidation,
}) => {
  const previousRentalHistory = toOptionalBoolean(watch('previousRentalHistory'))
  const rentalLedgerUploadLater = toOptionalBoolean(watch('rentalLedgerUploadLater')) === true

  const sessionId = watch('sessionId')
  const documents = watch('documents') ?? []
  const rentalLedgerDocument = documents.find(
    (document) => document.type === UPLOAD_SLOT_TYPES.rentalLedger
  )
  const rentalLedgerError = (errors?.documents as {message?: string} | undefined)?.message
  const [uploadMessage, setUploadMessage] = React.useState<string | null>(null)
  const [isUploadingLedger, setIsUploadingLedger] = React.useState(false)
  const [ledgerPreviewUrl, setLedgerPreviewUrl] = React.useState<string | undefined>(undefined)

  const hasRentalHistoryOptions = [
    {label: 'Yes, I have a rental ledger', value: true},
    {label: 'No, first time', value: false},
  ]

  const rentalLedgerSlot: UploadSlotDefinition = {
    type: UPLOAD_SLOT_TYPES.rentalLedger,
    label: 'Rental Ledger',
    required: true,
  }

  React.useEffect(
    () => () => {
      if (ledgerPreviewUrl) {
        URL.revokeObjectURL(ledgerPreviewUrl)
      }
    },
    [ledgerPreviewUrl]
  )

  const handleRentalHistoryChoice = (value: string | number | boolean) => {
    const rentalhistoryChoice = toOptionalBoolean(value)
    if (rentalhistoryChoice === undefined) {
      return
    }

    setValue('previousRentalHistory', rentalhistoryChoice, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })

    if (!rentalhistoryChoice) {
      setValue('rentalLedgerUploadLater', false, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
    }

    void triggerValidation?.()
  }

  const uploadLedgerFile = async (file: File) => {
    if (!sessionId) {
      setUploadMessage('Session expired. Please verify your mobile again.')
      return
    }

    setUploadMessage(null)
    setIsUploadingLedger(true)
    try {
      const uploaded = await uploadWizardDocument({
        sessionId,
        type: UPLOAD_SLOT_TYPES.rentalLedger,
        file,
        documentId: rentalLedgerDocument?.documentId,
      })

      setValue(
        'documents',
        [
          ...documents.filter((document) => document.type !== UPLOAD_SLOT_TYPES.rentalLedger),
          {
            ...rentalLedgerDocument,
            ...uploaded,
            type: UPLOAD_SLOT_TYPES.rentalLedger,
            filename: uploaded.filename || file.name,
            contentType: uploaded.contentType || file.type || 'application/octet-stream',
            verificationStatus: uploaded.verificationStatus || 'uploaded',
            uploadedAtUtc: uploaded.uploadedAtUtc || new Date().toISOString(),
          },
        ],
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        }
      )
      setValue('rentalLedgerUploadLater', false, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })

      if ((file.type || '').toLowerCase().startsWith('image/')) {
        if (ledgerPreviewUrl) {
          URL.revokeObjectURL(ledgerPreviewUrl)
        }
        setLedgerPreviewUrl(URL.createObjectURL(file))
      } else {
        if (ledgerPreviewUrl) {
          URL.revokeObjectURL(ledgerPreviewUrl)
        }
        setLedgerPreviewUrl(undefined)
      }
    } catch {
      setUploadMessage('Unable to upload rental ledger right now.')
    } finally {
      setIsUploadingLedger(false)
    }
  }

  const clearLedgerFile = async () => {
    if (sessionId && rentalLedgerDocument?.documentId) {
      try {
        await deleteWizardDocument({
          sessionId,
          documentId: rentalLedgerDocument.documentId,
        })
      } catch {
        setUploadMessage('Unable to remove rental ledger right now.')
        return
      }
    }

    setValue(
      'documents',
      documents.filter((document) => document.type !== UPLOAD_SLOT_TYPES.rentalLedger),
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      }
    )

    if (ledgerPreviewUrl) {
      URL.revokeObjectURL(ledgerPreviewUrl)
    }
    setLedgerPreviewUrl(undefined)
  }

  const saveRentalLedgerForLater = () => {
    if (rentalLedgerDocument?.filename?.trim()) {
      return
    }

    setUploadMessage(null)
    setValue('rentalLedgerUploadLater', true, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  const undoRentalLedgerUploadLater = () => {
    setUploadMessage(null)
    setValue('rentalLedgerUploadLater', false, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  const previewLedgerFile = async () => {
    if (!sessionId || !rentalLedgerDocument?.documentId) {
      return
    }

    try {
      const blob = await downloadWizardDocument(sessionId, rentalLedgerDocument.documentId)
      const previewUrl = URL.createObjectURL(blob)
      window.open(previewUrl, '_blank', 'noopener,noreferrer')
      window.setTimeout(() => URL.revokeObjectURL(previewUrl), 60000)
    } catch {
      setUploadMessage('Unable to preview rental ledger right now.')
    }
  }

  useEffect(() => {
    if (triggerValidation) triggerValidation()
  }, [])

  return (
    <div>
      <div className='wizard-hint-card mb-4 d-flex ' style={{padding: '20px'}}>
        <div className='b-icon'>
          <svg
            width='14'
            height='14'
            viewBox='0 0 20 20'
            fill='none'
            stroke='#9E7CCD'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M3 14l4-4 3 3 7-7M13 6h4v4'></path>
          </svg>
        </div>
        <span>
          <strong>Agents check this first.</strong> Rental history makes your application stand out.
        </span>
      </div>

      <div className='d-grid gap-4'>
        <WizardSectionCard title='Have you rented before? *' className='p-8'>
          <RadioButtonInput<IApplicationFormData>
            name='previousRentalHistory'
            label=''
            className='mt-5'
            control={control}
            errors={errors}
            required
            options={hasRentalHistoryOptions}
            value={previousRentalHistory}
            defaultValue={previousRentalHistory}
            onChange={handleRentalHistoryChoice}
          />

          {errors?.previousRentalHistory && (
            <div className='text-danger small mt-1'>
              {String(errors.previousRentalHistory.message)}
            </div>
          )}

          {previousRentalHistory && (
            <>
              <hr className='mt-10 mb-5' />
              <div className='mb-5'>
                <h5 className='subheader'>PREVIOUS RENTAL</h5>
                <span className='secondary text-muted'></span>
              </div>

              <div className='row g-3 mb-5'>
                <div className='col-12 col-md-6'>
                  <TextInput
                    name='previousPropertyManager'
                    label='Property manager name'
                    placeholder='Their name'
                    register={register}
                    errors={errors}
                  />
                </div>
                <div className='col-12 col-md-6'>
                  <MobileNumberInput
                    name='previousPropertyManagerPhone'
                    label='Property manager phone'
                    placeholder='Their number'
                    control={control}
                    errors={errors}
                  />
                </div>
              </div>

              <WheelInput
                name='previousRentWeekly'
                label='Weekly Rent'
                subLabel='per week'
                className='mb-12'
                control={control}
                errors={errors}
                required
                min={300}
                max={3000}
                defaultValue={500}
                step={5}
                prefix='$'
              />

              <SelectInput
                name='previousRentDuration'
                label='How long did you rent?'
                helperText='Check your ledger for the exact length'
                register={register}
                errors={errors}
                className='mb-5'
                optionsList={rentDurationOptions}
                required={true}
              />

              <div className='mt-3' data-wizard-scroll-target='rental-ledger'>
                <UploadSlotRow
                  allowPreview={false}
                  slot={rentalLedgerSlot}
                  document={rentalLedgerDocument}
                  documentName='Rental Ledger'
                  isUploading={isUploadingLedger}
                  previewUrl={ledgerPreviewUrl}
                  isSavedForLater={rentalLedgerUploadLater}
                  onUploadFile={(file) => {
                    void uploadLedgerFile(file)
                  }}
                  onPreview={() => {
                    void previewLedgerFile()
                  }}
                  onClear={() => {
                    void clearLedgerFile()
                  }}
                  onSaveForLater={saveRentalLedgerForLater}
                  onUndoSaveForLater={undoRentalLedgerUploadLater}
                />
                {!rentalLedgerUploadLater && (
                  <div className='text-muted small mt-2 ms-2'>PDF, JPG, or PNG</div>
                )}
                {rentalLedgerError && !rentalLedgerUploadLater && (
                  <div className='text-danger small mt-2'>{String(rentalLedgerError)}</div>
                )}
                {uploadMessage && <div className='text-danger small mt-2'>{uploadMessage}</div>}
              </div>
            </>
          )}
        </WizardSectionCard>
      </div>

      {previousRentalHistory === false && (
        <div className='alert alert-light mt-4 mb-0 border'>
          You can continue without rental history.
        </div>
      )}
    </div>
  )
}

export default RentalHistoryStep
